/* Hooks TanStack Query dos documentos legais (ADR-0774). A lista de um docKey é pequena por natureza
   (o histórico de termos de um produto tem poucas versões), então NÃO é paginada nem otimista: cada
   mutação invalida a query e o servidor volta a ser a fonte da verdade. Aqui isso não é preguiça — é
   o oposto do FAQ de propósito: mostrar otimisticamente uma versão como publicada quando o publish
   ainda pode falhar mentiria sobre um fato jurídico.

   O save de rascunho e a remoção são SEQUENCIAIS (um request por linha de idioma) para o erro ser
   determinístico: o primeiro 409 interrompe e chega ao onError com o código de imutabilidade, em vez
   de uma corrida decidir qual causa aparece. */
import type { CreateLegalVersionBody, LegalDocKey } from '@planuze/admin-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkDeleteLegalDrafts,
  createLegalVersion,
  deleteLegalVersionRow,
  listLegalVersions,
  publishLegalVersion,
  updateLegalVersion,
} from '../lib/legal-api.js';

function legalVersionsKey(docKey: LegalDocKey): readonly [string, string, LegalDocKey] {
  return ['legal', 'versions', docKey];
}

export function useLegalVersions(docKey: LegalDocKey) {
  return useQuery({
    queryKey: legalVersionsKey(docKey),
    queryFn: () => listLegalVersions(docKey),
  });
}

function useInvalidateLegal(docKey: LegalDocKey): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: legalVersionsKey(docKey) });
  };
}

export function useCreateLegalVersion(docKey: LegalDocKey) {
  const invalidate = useInvalidateLegal(docKey);
  return useMutation({
    mutationFn: (body: CreateLegalVersionBody) => createLegalVersion(docKey, body),
    onSettled: invalidate,
  });
}

/** Patch de UMA linha de idioma de um rascunho. Sem `version`/`locale`/`status`: identidade e
    transição de estado não são editáveis pelo update (§2/§3 do contrato). */
/* Não exportado: aparece só na assinatura da mutation deste módulo, e o caller passa o literal
   que o TS confere estruturalmente. */
interface LegalDraftRowPatch {
  readonly id: string;
  readonly title: string;
  readonly content: string;
}

export function useSaveLegalDraft(docKey: LegalDocKey) {
  const invalidate = useInvalidateLegal(docKey);
  return useMutation({
    mutationFn: async (rows: readonly LegalDraftRowPatch[]) => {
      for (const row of rows) {
        await updateLegalVersion(row.id, { title: row.title, content: row.content });
      }
    },
    onSettled: invalidate,
  });
}

/** Publica a versão. `effectiveAt` ausente = vigora agora; futuro = vigência programada. O id é de
    QUALQUER linha da versão — o servidor publica todas as linhas de (docKey, version). */
export function usePublishLegalVersion(docKey: LegalDocKey) {
  const invalidate = useInvalidateLegal(docKey);
  return useMutation({
    mutationFn: (input: { readonly id: string; readonly effectiveAt?: Date }) =>
      publishLegalVersion(input.id, input.effectiveAt),
    onSettled: invalidate,
  });
}

/** Remove um rascunho inteiro: um DELETE por linha de idioma (o endpoint é por linha). */
export function useDeleteLegalDraft(docKey: LegalDocKey) {
  const invalidate = useInvalidateLegal(docKey);
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      for (const id of ids) await deleteLegalVersionRow(id);
    },
    onSettled: invalidate,
  });
}

export function useBulkDeleteLegalDrafts(docKey: LegalDocKey) {
  const invalidate = useInvalidateLegal(docKey);
  return useMutation({
    mutationFn: (ids: readonly string[]) => bulkDeleteLegalDrafts(ids),
    onSettled: invalidate,
  });
}
